/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Button, ListItem, ListItemText, Typography, Tooltip } from '@mui/material';

import { LABEL } from './constants';
import { useStyles } from './styles';
import Loader from '../../shared/Loader';
import CustomSearch from '../CustomSearch';
import { selectDimensionPagination } from '../../store/role/policyDimension/dimensionSelector';
import { resetDimensionMemberData } from '../../store/role/policyDimension/dimensionActionCreator';
import ArrowChevronRight from '@jda/lui-common-icon-library-mui5/ui/ArrowChevronRight';
import { ArrowChevronLeft } from '@jda/lui-common-icon-library-mui5';

const CustomListCollector = ({ onSubmit, members, selectedMembers, onChangePage, setSearchQuery, level }: any) => {
  const dimensionInitialData = selectedMembers?.length > 0 && members ? members.filter((item: any) => !selectedMembers.some((member: any) => item.value === member.value)) : [];
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const observerLeftPanel: any = useRef();
  const dimensionPagination = useSelector(selectDimensionPagination);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [rightPanelQuery, setRightPanelQuery] = useState('');
  const [dimensionsLeft, setDimensionsLeft] = useState<any>(dimensionInitialData);
  const [dimensionsRight, setDimensionsRight] = useState<any>(selectedMembers || []);
  const [filteredDimensions, setFilteredDimensions] = useState<any>(dimensionsRight);

  const lastElementRefLeftPanel = useCallback((node: HTMLDivElement) => {
    // pagination for left panel
    if (observerLeftPanel.current) observerLeftPanel.current.disconnect();

    observerLeftPanel.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        // trigger page change on last element 
        // if total members are more than current members list
        if (dimensionPagination[level]?.totalCount > members.length) {
          onChangePage(dimensionPagination[level]?.pageNumber + 1);
        } else {
          setLoading(false);
        }
      }
    });
    if (node) observerLeftPanel.current.observe(node);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, dimensionPagination[level]?.pageNumber, dimensionPagination[level]?.totalCount]);

  useEffect(() => {
    // remove right panel members from left panel after pagination data
    if (members) {
      const tempDimensions = [...members];
      filteredDimensions.length && filteredDimensions.forEach((item: any) => {
        _.remove(tempDimensions, (dimension: any) => dimension.value === item.value);
      });
      setDimensionsLeft(_.uniqBy(tempDimensions, 'value'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);
  useEffect(() => {
    setDimensionsRight(selectedMembers || []);
    setFilteredDimensions(selectedMembers || []);
  }, [selectedMembers]);

  const handleSearchLeftPanel = (value: any) => {
    setQuery(value);
    setSearchQuery(value);
    dispatch(resetDimensionMemberData(level));
    onChangePage(0);
    setLoading(false);
  };

  // local search for right panel
  const handleSearchRightPanel = (value: any) => {
    setRightPanelQuery(value);
    if (!value.trim().length) setFilteredDimensions(dimensionsRight);
    const filteredRightDimensions = dimensionsRight.filter((dimension: { value: string }) =>
      dimension.value.toUpperCase().includes(value.toUpperCase())
    );
    setFilteredDimensions(filteredRightDimensions);
  };

  const onClearInput = () => {
    setQuery('');
    setSearchQuery('');
    onChangePage(0);
    setLoading(false);
    onSubmit(dimensionsRight);
  };

  const onSelectLeftSideDimension = (index: number) => {
    dimensionsLeft[index].isSelected = !dimensionsLeft[index].isSelected;
    setDimensionsLeft([...dimensionsLeft]);
  };

  const onSelectRightSideDimension = (index: number) => {
    dimensionsRight[index].isSelected = !dimensionsRight[index].isSelected;
    setDimensionsRight([...dimensionsRight]);
  };

  const addDimensions = () => {
    let dimensionsLeftTemp = [...dimensionsLeft];
    const selectedDimensions = dimensionsLeft.filter((item: any) => item.isSelected);
    const removeIsSelected = selectedDimensions.map((item: any) => {
      return { ...item, isSelected: false };
    });
    setDimensionsRight([...removeIsSelected, ...dimensionsRight]);
    onSubmit([...removeIsSelected, ...dimensionsRight]);
    setFilteredDimensions([...selectedDimensions, ...dimensionsRight]);
    _.remove(dimensionsLeftTemp, (item: any) => item.isSelected);
    setDimensionsLeft(dimensionsLeftTemp);
  };

  const removeDimensions = () => {
    let dimensionsRightTemp = [...dimensionsRight];
    const selectedDimensions = dimensionsRight.filter((item: any) => item.isSelected);
    const removeIsSelected = selectedDimensions.map((item: any) => {
      return { ...item, isSelected: false };
    });
    setDimensionsLeft([...removeIsSelected, ...dimensionsLeft]);
    _.remove(dimensionsRightTemp, (item: any) => item.isSelected);
    setFilteredDimensions(dimensionsRightTemp);
    setDimensionsRight(dimensionsRightTemp);
    onSubmit(dimensionsRightTemp);
  };

  const renderLeftRow = () => {
    return (
      <Box className={classes.listLeftPanel}>
        <Box className={classes.searchFieldWrap}>
          <CustomSearch
            value={query}
            placeHolder={LABEL.SEARCH}
            onClearInput={onClearInput}
            onSearchChange={handleSearchLeftPanel}
            size='medium'
          />
        </Box>
        <Box className={classes.listView}>
          {dimensionsLeft.length ? dimensionsLeft.map((item: any, index: number) => {
            return (
              <ListItem
                button
                key={index}
                data-testid={`left-panel-dimension-${index}`}
                onClick={() => onSelectLeftSideDimension(index)}
                className={item.isSelected ? classes.selectedListItem : classes.listItem}
              >
                {dimensionsLeft.length === index + 1 ?
                  <Tooltip title={item.value} placement="bottom">
                    <ListItemText ref={lastElementRefLeftPanel} primary={`${item.value}`} />
                  </Tooltip> : <Tooltip title={item.value} placement="bottom">
                    <ListItemText primary={`${item.value}`} />
                  </Tooltip>
                }
              </ListItem>
            );
          }) : <Typography className={classes.noItemsFound} align='center' color='textSecondary'>{LABEL.NO_ITEMS_FOUND}</Typography>}
          {loading &&
            <Box alignContent={'center'}>
              <Loader showLoader={true} isCenter />
            </Box>
          }
        </Box>
      </Box>
    );
  };

  const renderRightRow = () => {
    return (
      <Box className={classes.listRightPanel}>
        <Box className={classes.searchFieldWrap}>
          <CustomSearch
            value={rightPanelQuery}
            placeHolder={LABEL.SEARCH}
            onSearchChange={handleSearchRightPanel}
            onClearInput={() => setFilteredDimensions(dimensionsRight)}
            size='medium'
          />
        </Box>
        <Box className={classes.listView}>
          {filteredDimensions.length ? filteredDimensions.map((item: any, index: number) => {
            return (
              <ListItem
                button
                key={index}
                data-testid={`right-panel-dimension-${index}`}
                onClick={() => onSelectRightSideDimension(index)}
                className={item.isSelected ? classes.selectedListItem : classes.listItem}
              >
                <Tooltip title={item.value} placement="bottom">
                  <ListItemText primary={`${item.value}`} />
                </Tooltip>
              </ListItem>
            );
          }) : <Typography className={classes.noItemsFound} align='center' color='textSecondary'>{LABEL.NO_ITEMS_FOUND}</Typography>}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.listWrapperStyle}>
      {renderLeftRow()}
      <Box className={classes.centerBtnWrap}>
        <Button
          size='small'
          variant="text"
          color="secondary"
          onClick={addDimensions}
          data-testid="add-dimensions-btn"
        >
          <ArrowChevronRight color="inherit" />
        </Button>
        <Button
          size='small'
          variant="text"
          color="secondary"
          onClick={removeDimensions}
          data-testid="remove-dimensions-btn"
        >
          <ArrowChevronLeft color="inherit" />
        </Button>
      </Box>
      {renderRightRow()}
    </Box>
  )
};

export default CustomListCollector;